/**
 * useTabSync — Synchronizes useLabelStore with the active tab in useTabsStore.
 *
 * When switching tabs:
 *   1. Saves current useLabelStore state → old tab in useTabsStore
 *   2. Loads new tab data from useTabsStore → useLabelStore
 *
 * When useLabelStore changes:
 *   - Marks the active tab as dirty in useTabsStore
 *   - Syncs data back to the active tab periodically
 */

import { useEffect, useRef } from 'react';
import { useLabelStore } from '../store/useLabelStore';
import { useTabsStore } from '../store/useTabsStore';

export default function useTabSync() {
  const activeTabId = useTabsStore(s => s.activeTabId);
  const prevTabIdRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  // Vrai pendant qu'on APPLIQUE un onglet dans useLabelStore (switch/chargement) :
  // ces écritures programmatiques ne doivent PAS marquer l'onglet comme modifié
  // (sinon tout onglet ouvert/importé devient « dirty/rouge » à tort).
  const applyingRef = useRef(false);

  // ── On tab switch: save old tab, load new tab ──
  useEffect(() => {
    const prevId = prevTabIdRef.current;
    prevTabIdRef.current = activeTabId;

    if (!activeTabId) return;

    const tabsState = useTabsStore.getState();
    const labelState = useLabelStore.getState();

    // Save current label state to the OLD tab (if exists and different)
    if (prevId && prevId !== activeTabId) {
      const oldTab = tabsState.getTab(prevId);
      if (oldTab) {
        // Mise à jour DIRECTE de l'ancien onglet, sans toucher à son état « modifié ».
        // Un `updateActiveTabData.call({ activeTabId: prevId, … })` la précédait : la
        // fonction du store ne lit pas `this`, elle visait l'onglet actif — déjà le
        // NOUVEAU — et le marquait « modifié ». Tout onglet où l'on arrivait prenait
        // l'astérisque et demandait confirmation à la fermeture (2026-04-04 → 10-13).
        useTabsStore.setState(s => ({
          tabs: s.tabs.map(t => t.id === prevId ? {
            ...t,
            canvas: { ...labelState.canvas },
            elements: JSON.parse(JSON.stringify(labelState.elements)),
            aiImage: { ...labelState.aiImage },
            selectedElementIds: [...labelState.selectedElementIds],
            history: labelState.history,
            historyIndex: labelState.historyIndex,
          } : t),
        }));
      }
    }

    // Load new tab data into useLabelStore
    const newTab = tabsState.getTab(activeTabId);
    if (newTab) {
      applyingRef.current = true;   // chargement programmatique → ne pas marquer dirty
      useLabelStore.setState({
        canvas: { ...newTab.canvas },
        elements: JSON.parse(JSON.stringify(newTab.elements)),
        aiImage: { ...newTab.aiImage },
        selectedElementIds: [],
        history: newTab.history || [],
        historyIndex: newTab.historyIndex ?? -1,
      });
      applyingRef.current = false;

      // Initialize history if empty
      if (!newTab.history || newTab.history.length === 0) {
        useLabelStore.getState()._pushState('Ouverture du projet');
      }

      // Ajustement des cadres au contenu, demandé à l'import PDF. Il ne peut
      // se faire qu'ICI : mesurer un calque exige qu'il soit peint, et seul
      // l'onglet actif l'est. Un import par lot ouvre N onglets — chacun
      // s'ajuste la première fois qu'on le regarde. Texte seul : le fond d'un
      // gabarit est posé au format exact de la page et ne doit pas bouger.
      if (newTab.ajusterAuContenu) {
        useTabsStore.setState((s) => ({
          tabs: s.tabs.map((t) => t.id === newTab.id ? { ...t, ajusterAuContenu: false } : t),
        }));
        const ids = (newTab.elements || []).filter((e) => e && e.type === 'text').map((e) => e.id);
        if (ids.length) {
          import('../utils/fitFrame')
            .then(({ fitElementsToContent }) => fitElementsToContent(ids, {
              types: ['text'],
              nomHistorique: 'Import PDF · ajusté au contenu',
            }))
            .catch(() => {});
        }
      }
    }
  }, [activeTabId]);

  // ── Periodic sync: label store → active tab (every 2 seconds if dirty) ──
  useEffect(() => {
    // Compare uniquement les propriétés "données" du canvas — exclut pan/zoom
    // qui sont des ajustements de vue (panX, panY, scale) et ne doivent PAS
    // marquer le fichier comme modifié.
    const canvasIsDataChanged = (a, b) => {
      if (a === b) return false;
      const dataKeys = [
        'width_mm', 'height_mm', 'color_bg', 'margin_mm', 'bleed_mm',
        'showRulers', 'showGuides', 'showGrid',
        'snapToGrid', 'snapToGuides', 'showMargin', 'showBleed',
      ];
      return dataKeys.some(k => a?.[k] !== b?.[k]);
    };

    const unsubscribe = useLabelStore.subscribe((state, prevState) => {
      // Écriture programmatique (chargement d'onglet) → ne pas marquer dirty.
      if (applyingRef.current) return;
      // Detect actual data changes (not just selections, pan, zoom)
      const dataChanged =
        canvasIsDataChanged(state.canvas, prevState.canvas) ||
        state.elements !== prevState.elements ||
        state.aiImage !== prevState.aiImage;

      if (dataChanged) {
        const { activeTabId } = useTabsStore.getState();
        if (!activeTabId) return;

        // Mark dirty immediately
        useTabsStore.getState().markDirty(activeTabId);

        // Debounced sync to avoid performance issues
        if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = setTimeout(() => {
          const ls = useLabelStore.getState();
          useTabsStore.setState(s => ({
            tabs: s.tabs.map(t => t.id === s.activeTabId ? {
              ...t,
              canvas: { ...ls.canvas },
              elements: JSON.parse(JSON.stringify(ls.elements)),
              aiImage: { ...ls.aiImage },
              history: ls.history,
              historyIndex: ls.historyIndex,
            } : t),
          }));
        }, 2000);
      }
    });

    return () => {
      unsubscribe();
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, []);
}
